<template>
  <div class="timeline-email-slot ml-3">
    <div
      class="email-card"
      :class="[
        expanded ? 'email-card--expanded' : '',
        `email-card--${cardVariant}`,
        isOtherUserCard ? 'bg-light' : '',
      ]"
    >
      <div
        v-if="!expanded"
        class="email-collapsed-content email-head"
        @click="$emit('toggle')"
      >
        <div class="email-meta-row">
          <div class="email-sender">
            <div class="email-avatar" :class="`email-avatar--${cardVariant}`">
              <i
                v-if="cardVariant === 'reminder'"
                class="icon-bell-o"
                aria-hidden="true"
              ></i>
              <i
                v-else-if="cardVariant === 'sequence'"
                class="icon-bolt"
                aria-hidden="true"
              ></i>
              <span v-else>{{ personInitials }}</span>
            </div>
            <div class="sender-info">
              <div class="sender-name-row">
                <span class="sender-name">{{
                  item.name | formatContactName(item.email)
                }}</span>
                <span
                  class="timeline-email-badge"
                  :class="`timeline-email-badge--${cardVariant}`"
                >
                  <i
                    :class="
                      isType('emailreceived')
                        ? 'icon-arrow-down'
                        : 'icon-arrow-up'
                    "
                    aria-hidden="true"
                  ></i>
                  {{ typeBadgeLabel }}
                </span>
                <span
                  v-if="isType('emailreceived') && item.is_automatic == '1'"
                  class="badge badge-primary ml-1"
                  >{{ $t("lang.label.autoreply") }}</span
                >
                <span
                  v-if="isType('emailreceived') && isMailInBlack(item.comment)"
                  class="badge badge-danger ml-1"
                  >{{ $t("lang.label.antispam") }}</span
                >
              </div>
              <div
                v-if="subjectHtml"
                class="sender-action"
                v-html="subjectHtml"
              ></div>
            </div>
          </div>
          <div class="email-time-block">
            <div v-if="hasViewsCounter" class="email-time-block__meta">
              <span
                class="views-counter"
                :class="{ 'views-counter--viewed': viewsCount > 0 }"
                @click.stop="toggleViewsTooltip"
              >
                <i
                  :class="viewsCount > 0 ? 'icon-eye' : 'icon-eye-slash'"
                  aria-hidden="true"
                ></i>
                {{ viewsCount }}
              </span>
              <div
                v-if="viewsTooltipOpen"
                class="email-views-tooltip card p-2"
                @click.stop
              >
                <label>{{ viewsCount }} {{ $t("lang.label.views") }}</label>
                <small
                  class="cdarkgray d-block"
                  v-for="(datemail, viewIndex) in item.opendate"
                  :key="`${item.id}-view-${viewIndex}`"
                >
                  <i
                    class="text-primary icon-envelope-open-o mr-2"
                    aria-hidden="true"
                  ></i>
                  <span class="text-primary">{{ item.from[viewIndex] }}</span>
                  {{ datemail }}
                </small>
              </div>
            </div>
            <span class="email-time">{{ formattedDate }}</span>
            <i class="icon-arrow-down email-chevron" aria-hidden="true"></i>
          </div>
        </div>
      </div>

      <div v-else class="email-expanded-content">
        <div class="email-head">
          <div
            class="email-meta-row email-meta-row--toggle"
            @click="$emit('toggle')"
          >
            <div class="email-sender">
              <div class="email-avatar" :class="`email-avatar--${cardVariant}`">
                <i
                  v-if="cardVariant === 'reminder'"
                  class="icon-bell-o"
                  aria-hidden="true"
                ></i>
                <i
                  v-else-if="cardVariant === 'sequence'"
                  class="icon-bolt"
                  aria-hidden="true"
                ></i>
                <span v-else>{{ personInitials }}</span>
              </div>
              <div class="sender-info">
                <div class="sender-name-row">
                  <span class="sender-name">{{
                    item.name | formatContactName(item.email)
                  }}</span>
                  <span
                    class="timeline-email-badge"
                    :class="`timeline-email-badge--${cardVariant}`"
                  >
                    <i
                      :class="
                        isType('emailreceived')
                          ? 'icon-arrow-down'
                          : 'icon-arrow-up'
                      "
                      aria-hidden="true"
                    ></i>
                    {{ typeBadgeLabel }}
                  </span>
                  <span
                    v-if="isType('emailreceived') && item.is_automatic == '1'"
                    class="badge badge-primary ml-1"
                    >{{ $t("lang.label.autoreply") }}</span
                  >
                  <span
                    v-if="isType('emailreceived') && isMailInBlack(item.comment)"
                    class="badge badge-danger ml-1"
                    >{{ $t("lang.label.antispam") }}</span
                  >
                </div>
                <div class="sender-action">{{ expandedSubtitle }}</div>
              </div>
            </div>
            <div class="email-time-block">
              <div v-if="hasViewsCounter" class="email-time-block__meta">
                <span
                  class="views-counter"
                  :class="{ 'views-counter--viewed': viewsCount > 0 }"
                  @click.stop="toggleViewsTooltip"
                >
                  <i
                    :class="viewsCount > 0 ? 'icon-eye' : 'icon-eye-slash'"
                    aria-hidden="true"
                  ></i>
                  {{ viewsLabelExpanded }}
                </span>
                <div
                  v-if="viewsTooltipOpen"
                  class="email-views-tooltip card p-2"
                  @click.stop
                >
                  <label>{{ viewsCount }} {{ $t("lang.label.views") }}</label>
                  <small
                    class="cdarkgray d-block"
                    v-for="(datemail, viewIndex) in item.opendate"
                    :key="`${item.id}-view-exp-${viewIndex}`"
                  >
                    <i
                      class="text-primary icon-envelope-open-o mr-2"
                      aria-hidden="true"
                    ></i>
                    <span class="text-primary">{{ item.from[viewIndex] }}</span>
                    {{ datemail }}
                  </small>
                </div>
              </div>
              <span class="email-time">{{ formattedDate }}</span>
              <button
                type="button"
                class="email-collapse-btn"
                @click.stop="$emit('toggle')"
                :aria-label="
                  $t('lang.opportunities.timeline_email_collapse_aria')
                "
              >
                <i class="icon-arrow-up" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div
            v-if="isType('emailsentopportunity') && item.link_activity_type_id"
            class="mt-2"
          >
            <a>
              <span class="badge badge-secondary">
                {{ attachmentName(item.link_activity_type_id) }}
              </span>
            </a>
          </div>

          <div
            v-if="isType('emailreceived') && roleId == 1"
            class="mt-2"
          >
            <button class="btn btn-sm btn-primary" @click.stop="$emit('retag')">
              Re-TAG
            </button>
            &nbsp;
            <button
              class="btn btn-sm btn-primary"
              @click.stop="$emit('unlink')"
            >
              Unlink from Opportunity
            </button>
            <div v-if="showTags" class="card p-2">
              Result:
              <strong
                class="mx-1"
                v-for="(w, wi) in safeWorddetected"
                :key="`${item.id}-word-${wi}`"
                >{{ w }}</strong
              >
              <pre class="mt-2" v-html="highlightedBodyHtml"></pre>
              <div>
                <span
                  v-for="(tag, ind) in tagsForItem"
                  class="mx-1 badge text-white badge-success"
                  v-if="tag && ind !== 'analyzed'"
                  :key="`${item.id}-tag-ok-${ind}`"
                >
                  {{ ind }}
                </span>
                <span
                  v-for="(tag, ind) in tagsForItem"
                  class="mx-1 badge text-white badge-danger"
                  v-if="!tag"
                  :key="`${item.id}-tag-ko-${ind}`"
                >
                  {{ ind }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="subjectHtml" class="email-subject" v-html="subjectHtml"></div>

          <div class="email-body-wrap">
            <iframe
              v-if="isType('emailreceived')"
              class="h-iframe text-muted w-100 email-body-iframe"
              :srcdoc="emailBodyHtml"
            ></iframe>
            <pre
              v-if="
                isType('emailssent') ||
                isType('emailsent') ||
                isType('emailsentopportunity')
              "
              class="text-muted email-body-pre"
              v-html="emailBodyFormatted"
            ></pre>
          </div>
        </div>

        <div v-if="isAutomaticSent" class="email-footer">
          <span class="email-footer-info">
            <i class="icon-info-circle" aria-hidden="true"></i>
            {{ $t("lang.opportunities.timeline_email_footer_automatic") }}
          </span>
        </div>

        <div
          v-if="isType('emailreceived') && canShowReplyFooter"
          class="email-footer email-footer--actions"
        >
          <button
            v-if="!item.answer && !isReplyBlocked && activateSpark != '0'"
            type="button"
            class="btn btn-sm timeline-email-reply-btn"
            @click.stop="$emit('reply')"
          >
            <span class="icon-reply d-inline" aria-hidden="true"></span>
            {{ $t("lang.button.answer") }}
          </button>
          <span v-else>
            <Tooltip :text="$t('lang.notification.prospecting_no_exist')">
              <button
                v-if="!item.answer && !isReplyBlocked"
                type="button"
                class="btn btn-sm timeline-email-reply-btn"
                :disabled="true"
              >
                <span class="icon-reply d-inline" aria-hidden="true"></span>
                {{ $t("lang.button.answer") }}
              </button>
            </Tooltip>
          </span>
          <button
            v-if="!item.answer && isReplyBlocked"
            type="button"
            class="btn btn-sm btn-secondary"
            disabled
          >
            Oliver ON
          </button>
          <replyEmail
            v-show="isModalVisible"
            @close="$emit('close-reply')"
            :selected="selectedEmail"
            :user_id="userId"
            :closebtn="true"
            v-if="item.answer"
          ></replyEmail>
        </div>
      </div>
    </div>
    <div
      v-if="item.response_cc != null && item.response_cc != ''"
      class="small text-muted mt-1"
    >
      CC: {{ item.response_cc }}
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import replyEmail from "@/components/Emails/replyEmail.vue";
import Tooltip from "@/components/Common/Tooltip.vue";
import { campaignUtilsMixin } from "@/_mixins/";

export default {
  name: "TimelineEmailCard",
  components: {
    replyEmail,
    Tooltip,
  },
  mixins: [campaignUtilsMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    isOtherUserCard: {
      type: Boolean,
      default: false,
    },
    roleId: {
      type: [Number, String],
      default: null,
    },
    activateSpark: {
      type: String,
      default: "0",
    },
    userId: {
      default: null,
    },
    selectedEmail: {
      default: null,
    },
    isModalVisible: {
      type: Boolean,
      default: false,
    },
    canShowReplyFooter: {
      type: Boolean,
      default: false,
    },
    isReplyBlocked: {
      type: Boolean,
      default: false,
    },
    showTags: {
      type: Boolean,
      default: false,
    },
    tagsForItem: {
      type: Object,
      default: () => ({}),
    },
    worddetected: {
      type: Array,
      default: () => [],
    },
    highlightedBodyHtml: {
      type: String,
      default: "",
    },
    campaignTokens: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      viewsTooltipOpen: false,
    };
  },
  watch: {
    expanded(isExpanded) {
      if (!isExpanded) {
        this.viewsTooltipOpen = false;
      }
    },
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
      availableTokens: (state) => state.optionsList.all["available_tokens"],
    }),
    safeWorddetected() {
      return Array.isArray(this.worddetected) ? this.worddetected : [];
    },
    sentKind() {
      return this.sentEmailKind(this.item);
    },
    isAutomaticSent() {
      return this.sentKind !== null && this.sentKind !== "manual";
    },
    cardVariant() {
      if (this.item.type == "emailreceived") {
        return "received";
      }
      if (this.sentKind === "manual") {
        return "sent";
      }
      if (this.sentKind === "reminder" || this.sentKind === "reschedule") {
        return "reminder";
      }
      return "sequence";
    },
    typeBadgeLabel() {
      if (this.item.type == "emailreceived") {
        return this.$t("lang.opportunities.timeline_email_badge_received");
      }
      if (this.sentKind === "manual") {
        return this.$t("lang.opportunities.timeline_email_badge_sent_manual");
      }
      if (this.sentKind === "reminder") {
        return this.$t("lang.opportunities.timeline_email_badge_reminder");
      }
      if (this.sentKind === "reschedule") {
        return this.$t("lang.opportunities.timeline_email_badge_reschedule");
      }
      if (this.sentKind === "auto") {
        return this.$t("lang.opportunities.timeline_email_badge_auto_reply");
      }
      return this.$t("lang.opportunities.timeline_email_badge_sequence");
    },
    personInitials() {
      const displayName = this.$options.filters.formatContactName(
        this.item.name,
        this.item.email
      );
      return this.getPersonInitials(displayName);
    },
    subjectHtml() {
      if (!this.item.link) {
        return "";
      }
      return this.$sanitize(this.formatEmailTokens(this.item.link));
    },
    expandedSubtitle() {
      const counterpart = this.item.response_from || "";
      if (this.item.type == "emailreceived") {
        return `${this.$t("lang.label.answeredto")} ${counterpart}`;
      }
      if (this.sentKind === "manual") {
        return `${this.$t("lang.label.to")} ${counterpart}`;
      }
      return this.$t("lang.opportunities.timeline_email_subtitle_sent_auto", {
        name: counterpart,
      });
    },
    formattedDate() {
      return this.formatEmailCardDate(this.item.creation_date);
    },
    hasViewsCounter() {
      return (
        (this.item.type == "emailssent" ||
          this.item.type == "emailsentopportunity") &&
        this.item.opendate &&
        Array.isArray(this.item.opendate)
      );
    },
    viewsCount() {
      if (!this.item.opendate || !Array.isArray(this.item.opendate)) {
        return 0;
      }
      return this.item.opendate.length;
    },
    viewsLabelExpanded() {
      const count = this.viewsCount;
      if (count === 0) {
        return this.$t("lang.opportunities.timeline_email_unread");
      }
      if (count === 1) {
        return this.$t("lang.opportunities.timeline_email_one_view");
      }
      return this.$t("lang.opportunities.timeline_email_views_count", {
        count,
      });
    },
    emailBodyHtml() {
      return this.getEmail(this.item.comment, this.item.id);
    },
    emailBodyFormatted() {
      return this.formatEmailTokens(this.getEmail(this.item.comment));
    },
  },
  methods: {
    isType(type) {
      return this.item.type == type;
    },
    getPersonInitials(name) {
      if (!name || typeof name !== "string") {
        return "?";
      }
      const trimmed = name.trim();
      if (!trimmed || trimmed.indexOf("@") !== -1) {
        return "?";
      }
      return trimmed
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    isMailInBlack(body) {
      if (body) {
        return body.includes(".mailinblack");
      }
      return false;
    },
    formatEmailCardDate(stringDate) {
      if (!stringDate) {
        return "";
      }
      const dateArray = stringDate.split(/-|\s|:/);
      const date = new Date(
        dateArray[0],
        dateArray[1] - 1,
        dateArray[2],
        dateArray[3] || 0,
        dateArray[4] || 0,
        dateArray[5] || 0
      );
      if (isNaN(date.getTime())) {
        return "";
      }
      const day = date.getDate();
      const month = this.$t(
        `lang.opportunities.timeline_email_month_${date.getMonth() + 1}`
      );
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month}, ${hours}:${minutes}`;
    },
    toggleViewsTooltip() {
      this.viewsTooltipOpen = !this.viewsTooltipOpen;
    },
    attachmentName(url) {
      const splitUrl = url.split("/");
      return splitUrl[splitUrl.length - 1];
    },
    getEmail(body) {
      if (body) {
        body = body.replace("<a", '<a target="_blank" ');
        if (!body.includes("<p>")) {
          body = body.replace(/\n/g, "<br>");
        }
      }
      return body;
    },
    formatEmailTokens(message) {
      if (!message) {
        return "";
      }
      if (!this.currentUser) {
        return message;
      }
      return this.formatTokensAgency(message);
    },
  },
};
</script>

<style>
.h-iframe {
  height: 400px;
}

.timeline-email-slot {
  min-width: 0;
}

.email-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.email-card.bg-light {
  background-color: #f8f9fa !important;
}

.email-collapsed-content {
  cursor: pointer;
}

.email-meta-row--toggle {
  cursor: pointer;
}

.email-collapsed-content:hover {
  background: #fafafb;
}

.email-head {
  padding: 12px 16px;
}

.email-meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.email-sender {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.email-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
}

.email-avatar--received {
  background: #eeeefc;
  color: #3c3489;
}

.email-avatar--sent {
  background: #f4f4f7;
  color: #374151;
}

.email-avatar--reminder {
  background: #fef3c7;
  color: #92400e;
}

.email-avatar--sequence {
  background: #eef2ff;
  color: #3730a3;
}

.sender-info {
  min-width: 0;
  flex: 1;
}

.sender-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.sender-name {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.sender-action {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  word-break: break-word;
}

.email-time-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
  position: relative;
}

.email-time-block__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.email-time {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
}

.email-chevron {
  font-size: 14px;
  color: #9ca3af;
}

.timeline-email-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 5px;
  white-space: nowrap;
}

.timeline-email-badge--received {
  background: #eeeefc;
  color: #3c3489;
}

.timeline-email-badge--sent {
  background: #d1fae5;
  color: #065f46;
}

.timeline-email-badge--reminder {
  background: #fef3c7;
  color: #92400e;
}

.timeline-email-badge--sequence {
  background: #eef2ff;
  color: #3730a3;
}

.email-subject {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-top: 10px;
  line-height: 1.4;
  word-break: break-word;
}

.email-body-wrap {
  margin-top: 10px;
}

.email-body-pre,
.email-body-iframe {
  background: #fafafb;
  border-radius: 8px;
}

.email-body-wrap .email-body-pre {
  margin: 0;
  padding: 12px 14px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit !important;
}

.email-body-wrap .h-iframe.email-body-iframe {
  border: none;
  display: block;
  border-radius: 8px;
}

.views-counter {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  cursor: pointer;
}

.views-counter--viewed {
  color: #10b981;
}

.email-views-tooltip {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 5;
  min-width: 200px;
  margin-top: 4px;
}

.email-collapse-btn {
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}

.email-collapse-btn:hover {
  background: #f4f4f7;
  color: #374151;
}

.email-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fafafb;
  border-top: 1px solid #f4f4f7;
}

.email-footer--actions {
  border-top: 1px solid #e5e7eb;
}

.email-footer-info {
  font-size: 11px;
  color: #6b7280;
}

.timeline-email-reply-btn {
  background: #4b4be1;
  border-color: #4b4be1;
  color: #fff;
}

.timeline-email-reply-btn:hover:not(:disabled) {
  background: #3c3489;
  border-color: #3c3489;
  color: #fff;
}
</style>
